<template>
    <div v-if="!settings.is_sourcing_mode">
        <Breadcrumbs
            :crumbs="crumbs"
            :use-subheader="true"
            class="hidden at1024:flex bg-gray-tier-4 border-keyline-primary"
        >
            <template #subheader>
                <div class="type-primary hidden items-center gap-0.5 text-xl font-bold at1024:flex">
                    <button
                        @click="toggleManufacturerModal($event, {manufacturers: manufacturers, tags: tags, isPriceBooks: true})"
                        class="flex items-center hover:text-red"
                    >
                        {{ manufacturer.name }}
                        <BaseSvg name="expand-more-thick" class="w-6 h-6" aria-hidden="true" />
                        <span class="sr-only">Choose another manufacturer</span>
                    </button>
                </div>
            </template>
        </Breadcrumbs>
        <div class="w-full flex flex-col at1024:flex-row gap-8 p-8">
            <ManufacturerAside :manufacturer="manufacturer" :vendor-categories="vendorCategories"/>
            <main class="grow">
                <div class="w-full">
                    <h1 class="font-bold text-2xl at1024:text-3xl pb-4">
                        {{ manufacturer.name }}
                    </h1>
                    <!-- No price book -->
                    <h3
                        v-if="!manufacturer.resources['Price Books']?.length"
                        class="text-left text-gray-400 my-8 mx-4"
                    >
                        {{ $t("Call for pricing", { phone: "800-847-5625" }) }}
                    </h3>
                    <div v-for="(section, key) in sortedResources" class="w-full border-t">
                        <h5 class="font-bold my-3" :dusk="`resource-section-${key.toLowerCase().replace(/\s+/g, '-')}`">{{ key }}</h5>
                        <ul>
                            <li
                                v-for="file in sortSections(section)"
                                class="flex gap-6 items-start px-4 py-3 hover:bg-gray-125"
                            >
                                <span class="flex mr-auto">
                                    <BaseSvg
                                        class="h-6 mr-3 mt-px shrink-0 w-6"
                                        :name="config.resources.icons[key] ?? 'icon-document'"
                                        aria-hidden="true"
                                    />
                                    <a
                                        :href="marketingAssetUrl(file.asset?.external_id)"
                                        class="leading-tight underline-offset-4 hover:underline"
                                        target="_blank"
                                    >
                                        {{ file.name }}
                                    </a>
                                </span>
                                <span class="hidden shrink-0 text-right w-24 whitespace-nowrap at1024:block">
                                {{
                                    file?.asset?.updated_at
                                        ? DateTime.fromISO(file?.asset?.updated_at).toLocaleString(DateTime.DATE_MED)
                                        : "–"
                                }}
                                </span>
                                <button
                                    @click.stop.prevent="
                                        forceDownload(
                                            $event,
                                            marketingAssetUrl(file.asset?.external_id),
                                            _.kebabCase(file.name) + '.pdf'
                                        )
                                    "
                                    class="group hidden -translate-y-px at1024:block"
                                >
                                    <BaseSvg
                                        name="icon-export-file"
                                        class="shrink-0 fill-black group-hover:fill-red"
                                        aria-hidden="true"
                                    />
                                    <span class="sr-only">Download the {{ file.name }}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </main>
        </div>
        <Meta
            :title="`${manufacturer.name} – Price Books`"
            :description="'We carry a wide range of products from ' + manufacturer.name + '. View technical resources, security products & price books.'"
            :meta="manufacturer.meta"
            :ogImage="manufacturer.logo?.external_id"
            :ogImagePadTransform="true"
            :jsonLdMainEntity="jsonLdMainEntityManufacturer(manufacturer, [manufacturer.name])"
            :jsonLdBreadcrumbs="crumbs"
        />
    </div>

    <!-- sm -->
    <LeftAsideSubLayout v-else :title="`Resources: Price Books and Catalogs / ${manufacturer.name}`" :useBackButton="true" :backButtonUrl="route('web.catalog.price-books.index')">
        <template #aside>
            <div class="p-6 flex flex-col gap-4 overflow-auto">
                <ManufacturerAside :manufacturer="manufacturer" :vendor-categories="vendorCategories" :manufacturers="manufacturers"/>
            </div>
        </template>
        <template #default>
            <div class="w-full p-10 pt-0">
                <PriceBookCard :manufacturer="manufacturer" :index="index" :individual="true" />
            </div>
        </template>
    </LeftAsideSubLayout>
    <Meta
        :title="`${manufacturer.name} – Price Books`"
        :description="'We carry a wide range of products from ' + manufacturer.name + '. View technical resources, security products & price books.'"
        :meta="manufacturer.meta"
        :ogImage="manufacturer.logo?.external_id"
        :ogImagePadTransform="true"
        :jsonLdMainEntity="jsonLdMainEntityManufacturer(manufacturer, [manufacturer.name])"
        :jsonLdBreadcrumbs="crumbs"
    />
</template>

<script setup>
import {Breadcrumbs} from "@/Themes/Marketing";
import {useAssetTransforms, useDownload, useJsonLd, useManufacturerModal, useUser} from "@/Composables";
import {BaseSvg, ManufacturerAside, Meta, PriceBookCard} from "@/Components";
import {LeftAsideSubLayout} from "@/Layouts";
import config from "@/config";
import _ from "lodash";
import {DateTime} from "luxon";
import {computed} from "vue";

const { settings } = useUser();
const { jsonLdMainEntityManufacturer } = useJsonLd();
const { toggleManufacturerModal } = useManufacturerModal();
const { marketingAssetUrl } = useAssetTransforms();
const { forceDownload } = useDownload();

const props = defineProps(['manufacturer','manufacturers','tags','vendorCategories','crumbs']);

const sortedResources = computed(() => {
    let sortedKeys = _.sortBy(Object.keys(props.manufacturer.resources));
    return _.pick(props.manufacturer.resources, sortedKeys);
});

const sortSections = (sections) => {
    if(sections?.length > 1) {
        return sections?.sort((a, b) => a.name?.localeCompare(b?.name))
    }
    return sections;
};
</script>
